export function canonicalIntentKey(category) {
  return String(category || "uncategorized")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "uncategorized";
}

const promptMetadataKey = (row) => [
  String(row.category || "").trim().toLowerCase(),
  String(row.prompt_type || "").trim().toLowerCase(),
  row.prompt_weight == null ? "<null>" : String(Number(row.prompt_weight)),
  String(row.llm_provider || "").trim().toLowerCase(),
  String(row.model_version || "").trim().toLowerCase(),
  row.prompt_fingerprint == null ? "<missing-fingerprint>" : String(row.prompt_fingerprint).trim().toLowerCase(),
].join("|");

/**
 * Validate the observable score cohort before reporting historical movement.
 * Prompt IDs are necessary but insufficient: category/type/weight and the
 * primary score provider/model must also match, and both runs must be complete.
 */
export function assessHistoricalCohort(currentRows, previousRows) {
  if (!Array.isArray(currentRows) || !Array.isArray(previousRows) || !currentRows.length || !previousRows.length) {
    return { state: "insufficient", reason: "missing-results", matchedCount: 0 };
  }

  if ([...currentRows, ...previousRows].some((row) => row.status !== "completed" || row.visibility_signal === "error")) {
    return { state: "incomplete", reason: "incomplete-or-error-results", matchedCount: 0 };
  }

  const hasRequiredMetadata = (rows) => rows.every((row) => (
    row.prompt_id && row.category && row.prompt_type && row.prompt_weight != null &&
    Number.isFinite(Number(row.prompt_weight)) &&
    row.llm_provider && row.model_version
  ));
  if (!hasRequiredMetadata(currentRows) || !hasRequiredMetadata(previousRows)) {
    return { state: "insufficient", reason: "missing-methodology-metadata", matchedCount: 0 };
  }

  const toMap = (rows) => {
    const result = new Map();
    for (const row of rows) {
      if (result.has(row.prompt_id)) return null;
      result.set(row.prompt_id, row);
    }
    return result;
  };
  const currentById = toMap(currentRows);
  const previousById = toMap(previousRows);
  if (!currentById || !previousById) {
    return { state: "insufficient", reason: "duplicate-prompt-results", matchedCount: 0 };
  }
  if (currentById.size !== previousById.size || [...currentById.keys()].some((id) => !previousById.has(id))) {
    return { state: "incompatible", reason: "different-prompt-cohort", matchedCount: 0 };
  }

  for (const [promptId, current] of currentById) {
    const previous = previousById.get(promptId);
    if (promptMetadataKey(current) !== promptMetadataKey(previous)) {
      return { state: "incompatible", reason: "prompt-methodology-changed", matchedCount: currentById.size };
    }
    if ((current.prompt_score == null) !== (previous.prompt_score == null) ||
        (current.visibility_signal == null) !== (previous.visibility_signal == null) ||
        (current.brand_mentions == null) !== (previous.brand_mentions == null)) {
      return { state: "incompatible", reason: "measurement-eligibility-changed", matchedCount: currentById.size };
    }
  }

  return { state: "comparable", reason: null, matchedCount: currentById.size };
}

export function compareIntentGroups(currentCategories, previousCategories, comparisonState) {
  if (comparisonState === false || comparisonState === "baseline") {
    return { state: "baseline", comparableCount: 0, currentCount: 0, improved: 0, decreased: 0, flat: 0 };
  }
  if (comparisonState !== true && comparisonState !== "comparable") {
    return { state: comparisonState || "insufficient", comparableCount: 0, currentCount: currentCategories.length, improved: 0, decreased: 0, flat: 0 };
  }

  const current = currentCategories.filter((item) => Number.isFinite(item.avgScore));
  const previousByKey = new Map(
    previousCategories
      .filter((item) => Number.isFinite(item.avgScore))
      .map((item) => [canonicalIntentKey(item.category), item])
  );
  const comparable = current.filter((item) => previousByKey.has(canonicalIntentKey(item.category)));

  const totals = comparable.reduce((result, item) => {
    const previous = previousByKey.get(canonicalIntentKey(item.category));
    const change = item.avgScore - previous.avgScore;
    if (change > 0) result.improved += 1;
    else if (change < 0) result.decreased += 1;
    else result.flat += 1;
    return result;
  }, { improved: 0, decreased: 0, flat: 0 });

  return {
    ...totals,
    currentCount: current.length,
    comparableCount: comparable.length,
    state: comparable.length === 0 ? "unavailable" : "comparable",
  };
}
